<template>
  <v-card class="matter-info"
          :class="{'mobile-view': isMobile}"
          elevation="2">
    <VcitaAlert v-if="showBalanceAlert"
                type="info"
                :body-text="$t('client.matters.matter_page.alert_payment', {amount: openPayments.value_h})"
                :close-button="true"/>
    <ExpansionPanels :items="panelItems"
                     :active-panels="activePanels"
                     :direction="direction"
                     @panel-change="onPanelChange"
                     :on-hover-color="hoverColor"
                     :disable-hover="isMobile">

      <template slot="title"
                slot-scope="headerSlot"
                v-if="headerSlot.item.contentCmp === 'MatterDetails'">

        <v-tooltip top
                   content-class="matter-name-tooltip"
                   :disabled="isMobile">
          <template v-slot:activator="{ on, attrs }">
            <div class="matter-name-title"
                 v-on-hover-color="{color: hoverColor, disabled: isMobile}"
                 v-bind="attrs"
                 v-on="on">
              {{matter.display_name}}
            </div>
          </template>
          <span>{{matter.display_name}}</span>
        </v-tooltip>

      </template>

      <template slot="header"
                slot-scope="headerSlot"
                v-if="headerSlot.item.contentCmp === 'MatterDetails'">
        <div class="header-cmp">

          <v-tooltip top
                     content-class="edit-tooltip"
                     :disabled="isMobile">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="edit-button"
                      @click="handleEditButtonClick"
                      v-bind="attrs"
                      v-on="on">
                icon-pencil-s
              </v-icon>
            </template>
            <span>{{$t('common.words.edit')}}</span>
          </v-tooltip>

        </div>
      </template>

      <template slot="content"
                slot-scope="contentSlot">
        <component :is="contentSlot.item.contentCmp"
                   :matter="matter"/>
      </template>

    </ExpansionPanels>

    <FabBtn v-if="showFab && isMobile"
            :bgColor="brandBgColor"
            :textColor="brandTxtColor"
            custom-class="bottom-right"
            @click="handleBtnClick()"
            icon="icon-plus-s"/>

    <span class="fab-btn bottom-right add-payment-btn-desktop"
          v-if="showFab && !isMobile">
      <DropdownMenu :top="true"
                    :left="!isRightToLeft"
                    :right="isRightToLeft"
                    :offsetY="true"
                    :items="actions"
                    @item-clicked="actionsClicked">
        <v-btn :color="brandBgColor" rounded fab>
          <v-icon :color="brandTxtColor">icon-plus-s</v-icon>
        </v-btn>
      </DropdownMenu>
    </span>

  </v-card>
</template>

<script>
  import {MatterDetails} from '@/modules/matters/components';
  import {ExpansionPanels, FabBtn, DropdownMenu} from "@/modules/common/components";
  import NotePreview from "./NotePreview";
  import MatterSummary from "./MatterSummary";
  import {storageService} from "@/modules/common/services";
  import {mapGetters} from "vuex";
  import matterActionsService from "@/modules/matters/services/matterActionsService";
  import VcitaAlert from "@/modules/common/components/VcitaAlert/VcitaAlert";

  export default {
    name: 'MatterInfo',
    components: { VcitaAlert, ExpansionPanels, NotePreview, MatterDetails, MatterSummary, FabBtn, DropdownMenu },
    props: {
      matter: {
        type: Object
      },
      direction: {
        type: String,
        default: 'ltr'
      },
      showFab: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        showBalanceAlert: false
      }
    },
    created() {
      this.$frontageApi.listen('fetch-notes', this.refreshNotes);
      this.fetchNotes();
    },
    watch: {
      openPayments() {
        this.showBalanceAlert = this.openPayments.value > 0;
      }
    },
    computed: {
      ...mapGetters(['isMobile']),
      ...mapGetters("brandModule", ["hoverColor"]),
      ...mapGetters(['isRightToLeft']),
      ...mapGetters("accountModule", ["business"]),
      ...mapGetters('mattersModule', ['paymentsSummary', 'currContact']),
      openPayments() {
        return this.paymentsSummary && this.paymentsSummary.open || {value: 0};
      },
      activePanels() {
        return storageService.get('infoActivePanels') || this.defaultActivePanels;
      },
      defaultActivePanels() {
        let tabs = ['MatterSummary'];
        this.matter.note && tabs.push('NotePreview');
        this.matter.fields.length && tabs.push('MatterDetails');
        return tabs;
      },
      panelItems() {
        return [
          { contentCmp: 'MatterDetails', id: 'MatterDetails', trackingLabel: 'matter info' },
          { name: this.$t('client.matters.matter_page.recent_note_title'), contentCmp: 'NotePreview', id: 'NotePreview' , trackingLabel: 'recent note' },
          { name: this.$t('client.matters.matter_page.summary.summary_title'), contentCmp: 'MatterSummary', id: 'MatterSummary', trackingLabel: 'activity highlights' }
        ]
      },
      brandBgColor() {
        return this.$store.getters['brandModule/secondaryColor'];
      },
      brandTxtColor() {
        return this.$store.getters['brandModule/textColor'];
      },
      actions() {
        return matterActionsService.getActions('message', 'appointment', 'register_to_event', 'products', 'document', 'invoice', 'estimate', 'packages', 'accept_payment', 'record_payment', 'note', 'invite');
      },
    },
    methods: {
      takePayment() {
        this.$frontageApi.send('open_close_balance_dialog', {client_uid: this.currContact.uid, matter_uid: this.matter.uid})
      },
      fetchNotes() {
        this.$store.dispatch({type:'mattersModule/loadMatterSummary'});
      },
      refreshNotes() {
        this.$store.dispatch({type:'mattersModule/refreshMatterSummary'});
      },
      handleEditButtonClick() {
        this.$frontageApi.send('matter_action', {action: 'edit_matter', uid: this.matter.uid});
      },
      onPanelChange(newVal, oldVal) {
        storageService.save('infoActivePanels', newVal.map(panel => this.panelItems[panel].contentCmp));
        const collapsed = oldVal.filter(panel => !newVal.includes(panel));
        const expanded = newVal.filter(panel => !oldVal.includes(panel));

        for (let i = 0; i < collapsed.length; i++) {
          this.$track(`Collapse ${this.panelItems[collapsed[i]].trackingLabel}`);
        }

        for (let i = 0; i < expanded.length; i++) {
          this.$track(`Expand ${this.panelItems[expanded[i]].trackingLabel}`);
        }
      },
      handleBtnClick() {
        this.$frontageApi.send('matter_action', {action: 'new_matter_action'});
      },
      actionsClicked(e) {
        this.$frontageApi.send('matter_action', {action: 'all_actions', action_name: e.action});
      },
    },
    destroyed() {
      this.$frontageApi.remove('fetch-notes', this.refreshNotes)
    },
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_mixins.scss";
  @import "@/assets/scss/abstract/_variables.scss";

  .matter-info {
    height: 100%;
    width: 344px;
    min-width: 344px;
    @include i-margin-mirror(0, 20px);
    @include hide-scrollbar;
    overflow: scroll;

    @include under-supported-size {
      width: 286px;
      min-width: 286px;
    }

    &.mobile-view {
      width: 100%;
    }

    .matter-name-title {
      max-width: calc(100% - 20px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      height: 16px;
      flex: none;
    }

    .edit-button {
      font-size: 18px;
      @include i-position-mirror(right, 18px);
      top: 18px;
      height: 30px;
      width: 30px;
      position: absolute;
      color: $grey-color-5;
      border-radius: 50%;
      transition: color 0.2s, background-color 0.2s;

      .matter-page-wrapper &, .matter-pane-desktop & {

        &:hover {
          color: $navy-color-1;
          background-color: $grey-color-10;
        }
      }
    }

    .fab-btn {
      position: fixed;

      .v-btn {
        height: 50px;
        width: 50px;
      }
    }
    ::v-deep .vcita-alert-info{
      margin-block-end: 0;
      border-radius: 0;
    }
  }

  .edit-tooltip {
    font-family: var(--primary-font-family);
    font-size: 11px;
    line-height: 18px;
    margin-top: 13px;
    padding: 1px 10px;
  }

  .matter-name-tooltip {
    font-family: var(--primary-font-family);
    font-size: 12px;
    line-height: 18px;
    margin-top: 8px;
    padding: 2px 10px;
  }
</style>
